import { buttonVariants } from '@/app/common/components/button/buttonVariants'
import { Card, CardContent, CardHeader } from '@/app/common/components/Card'
import MultiQueryPageLoader from '@/app/common/components/MultiQueryPageLoader'
import { WarningIcon } from '@/app/common/icons/support'
import { academicTermsQueries } from '@/queries/academic_terms/queries'
import { ProgramQueries } from '@/queries/program/queries'
import { AppRoutePaths } from '@/routing/route_paths'
import { selectSelectedProgram } from '@/storage/programs/slice'
import { useQuery } from '@tanstack/react-query'
import { useSelector } from 'react-redux'
import { Link } from 'react-router-dom'
import {
  getAcademicTermsByTermType,
  getSelectedProgramYear,
  hasProgramStartDatePassed,
} from '../utils'
import AcademicTermsForm from './_components/create-academic-term-form/create-academic-term-form'

export default function AcademicTermsPage() {
  const selectedProgramYearId = useSelector(selectSelectedProgram).id
  const awardDistributionPeriodsQuery = useQuery(
    academicTermsQueries.getProgramAcademicTerms(selectedProgramYearId)
  )
  const globalAcademicTermsQuery = useQuery(
    academicTermsQueries.getGlobalAcademicTerms()
  )

  const programDetailsQuery = useQuery(ProgramQueries.FetchGlobalPrograms())

  return (
    <MultiQueryPageLoader
      queries={
        [
          awardDistributionPeriodsQuery,
          globalAcademicTermsQuery,
          programDetailsQuery,
        ] as const
      }
    >
      {([academicTerms, globalAcademicTerms, programDetails]) => {
        const selectedProgramYear = getSelectedProgramYear(
          programDetails,
          selectedProgramYearId
        )

        if (!selectedProgramYear) {
          return (
            <div>
              <WarningIcon className="text-3xl text-primary-500" />
              <h3 className="text-2xl font-medium text-gray-800">
                Selected program year not found
              </h3>
              <p>Please contact support to get help with this issue.</p>
            </div>
          )
        }

        const hasStartDatePassed = hasProgramStartDatePassed(
          selectedProgramYear.program_start_date,
          selectedProgramYear.global_timezone_code
        )
        if (hasStartDatePassed) {
          return (
            <div className="pt-20 max-w-lg mx-auto">
              <Card>
                <CardHeader>
                  <WarningIcon className="text-3xl text-primary-500" />
                  <h3 className="text-2xl font-medium text-gray-800">
                    Term system is locked for your current school year.
                  </h3>
                </CardHeader>
                <CardContent className="space-y-4 text-gray-700 ">
                  <p>
                    Why is this locked? Once a term becomes active, the system
                    is locked for the entire school year to maintain consistency
                    for students and financial aid.
                  </p>
                  <p>
                    You can select a different system when setting up the next
                    school year.
                  </p>
                  <div className="pt-6">
                    <Link
                      to={AppRoutePaths.academic_terms.view}
                      className={buttonVariants({
                        variant: 'primary',
                        size: 'lg',
                        className: 'w-full',
                      })}
                    >
                      Ok Understood, Take me back
                    </Link>
                  </div>
                </CardContent>
              </Card>
            </div>
          )
        }

        const selectedAcademicTerms = getAcademicTermsByTermType(
          academicTerms,
          selectedProgramYear
        )
        return (
          <AcademicTermsForm
            programSettings={selectedProgramYear}
            programAcademicTerms={selectedAcademicTerms}
            globalAcademicTerms={globalAcademicTerms}
          />
        )
      }}
    </MultiQueryPageLoader>
  )
}
